import { Injectable } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { environment as env } from '@env';
import { ApiService } from './api.service';
import { ToastrService } from './toast-service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;

  constructor(private api: ApiService, private toastr: ToastrService) {}

  // Called once after login: requests browser notification permission,
  // gets an FCM token, registers it with the backend, and starts listening
  // for foreground pushes so an already-open tab shows a toast.
  async init(): Promise<void> {
    if (!env.firebase?.apiKey || typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

      this.app = this.app || initializeApp(env.firebase);
      this.messaging = this.messaging || getMessaging(this.app);

      const token = await getToken(this.messaging, {
        vapidKey: env.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        this.api.post('/users/fcm-token', { fcmToken: token }).subscribe();
      }

      onMessage(this.messaging, (payload) => {
        const title = payload.notification?.title || 'New notification';
        const body = payload.notification?.body || '';
        this.toastr.info(body, title);
      });
    } catch (error) {
      console.error('[NotificationService] Failed to initialize push notifications:', error);
    }
  }

  list(page = 1, limit = 20) {
    return this.api.get(`/notifications?page=${page}&limit=${limit}`);
  }

  markRead(id: number) {
    return this.api.put(`/notifications/${id}/read`);
  }
}
